<template>
  <ModalRoot v-model="isOpen">
    <template #body="{ close }">
      <ModalBodyV2 @close="close">
        <template #title>
          <h2 class="text-2xl font-serif text-text self-center">
            {{ $t("channel.contests.quiz.selectAnswer.title") }}
          </h2>
        </template>

        <p class="font-serif text-text text-lg leading-snug break-good">
          {{ questionTitle }}
        </p>

        <div
          class="flex flex-col gap-2 overflow-y-auto max-h-[55vh] pr-1"
          role="listbox"
        >
          <ChannelQuizOption
            v-for="option in options"
            :key="option.id"
            size="lg"
            :is-correct="
              quizCardState.currentQuestion?.status === QuestionStatus.Done &&
              option.is_correct === true
            "
            :read-only="isReadOnly"
            :is-poll="quizCardState.isPoll"
            :enable-participation-count="enableParticipationCount"
            :question-participation-count="questionParticipationTotalCount ?? 0"
            :question-option-participation-count="
              questionParticipationCountMap?.[option.id] ?? 0
            "
            :is-submitted="
              selectedOptionId === option.id ||
              quizCardState.currentQuestion?.selected_option_id === option.id
            "
            :created-by-id="
              quizCardState.quiz?.created_by_id ??
              quizCardState.quiz?.created_by?.id
            "
            :option="option"
            @click="onOptionClick(option.id)"
          />
        </div>

        <div v-if="isReadOnly" class="flex pt-1">
          <ContestButton variant="subtle" class="!h-11" @click="close">
            {{ $t("common.close") }}
          </ContestButton>
        </div>

        <div v-else class="flex gap-2 pt-1">
          <ContestButton
            variant="subtle"
            class="!h-11"
            :disabled="voting"
            @click="close"
          >
            {{ $t("common.close") }}
          </ContestButton>

          <ContestTooltip side="top" :disabled="!!selectedOptionId">
            <template #trigger>
              <div class="flex flex-1">
                <ContestButton
                  variant="primary"
                  class="!h-11"
                  :class="{
                    'opacity-50 saturate-50 cursor-not-allowed':
                      !selectedOptionId,
                  }"
                  :aria-disabled="!selectedOptionId"
                  :is-loading="voting"
                  @click="onRespondClick"
                >
                  {{ $t("channel.contests.quiz.actions.respond") }}
                </ContestButton>
              </div>
            </template>
            <p>{{ $t("channel.contests.quiz.selectAnswer.selectFirst") }}</p>
          </ContestTooltip>
        </div>
      </ModalBodyV2>
    </template>
  </ModalRoot>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { QuestionStatus } from "@/generated/graphql";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import ModalBodyV2 from "@/components/ui/modal/modal-body-v2.vue";
import ChannelQuizOption from "@/components/contest/channel-quiz-options.vue";
import ContestButton from "../contest-button.vue";
import ContestTooltip from "../contest-tooltip.vue";
import { useJustOneOpenModal } from "@/composables/buses/use-modal-bus";
import { useQuizCardState } from "../quiz-card/quiz-card-state";
import { useQuizVote } from "../quiz-card/use-quiz-vote";
import { useGatingForAction } from "@/features/gating/evaluation/use-gating-for-action";
import { ChannelPermission } from "@f3/shared/permissions";
import { ExperienceAction, ExperienceType } from "@/enums/experience-type.enum";
import { useObjectTranslation } from "@/stores/translation.store";

const props = defineProps<{
  disabled: boolean;
  enableParticipationCount: boolean;
  questionParticipationTotalCount: number | undefined | null;
  questionParticipationCountMap: Record<string, number> | undefined | null;
}>();

const emit = defineEmits<{
  answerSubmitted: [];
}>();

const isOpen = defineModel<boolean>({ required: true });
useJustOneOpenModal(isOpen);

const quizCardState = useQuizCardState();

// Deliberately not `quizCardState.selectedOptionId` — that would also open
// the card's respond bar, and the modal owns its own submit CTA.
const selectedOptionId = ref<string | null>(null);

const options = computed(() => quizCardState.currentQuestion?.options ?? []);

const isReadOnly = computed(
  () =>
    props.disabled ||
    quizCardState.currentQuestion?.status === QuestionStatus.Done ||
    !!quizCardState.currentQuestion?.selected_option_id,
);

const { text: questionTitle } = useObjectTranslation(
  computed(() => {
    const question = quizCardState.currentQuestion;
    if (!question?.title) return null;

    return {
      authorUserId:
        quizCardState.quiz?.created_by_id ?? quizCardState.quiz?.created_by?.id,
      originalText: question.title,
      objectAccessors: { objectId: question.id, objectProperty: "title" },
      translatedText: question.quiz_question_translations?.[0]?.title,
    };
  }),
);

const { handler: selectOption } = useGatingForAction(
  ChannelPermission.interactInContests,
  (option_id: string) => {
    selectedOptionId.value = option_id;
  },
  {
    gatingExperience: {
      experienceId: quizCardState.quiz?.id,
      experienceType: ExperienceType.Quiz,
      experienceAction: ExperienceAction.SELECT_OPTION,
      experienceCreatorId: quizCardState.quiz?.created_by?.id ?? undefined,
      experienceCreatorUsername:
        quizCardState.quiz?.created_by?.username ?? undefined,
      experienceCommunityIds: quizCardState.quiz?.quiz_communities
        ?.map((c) => c.community?.id)
        .filter((id): id is string => id != null),
      experienceCommunityNames: quizCardState.quiz?.quiz_communities
        ?.map((c) => c.community?.name)
        .filter((name): name is string => name != null),
    },
  },
);

function onOptionClick(optionId: string) {
  if (isReadOnly.value) return;
  selectOption(optionId);
}

const { submitAnswer, voting } = useQuizVote(() => {
  selectedOptionId.value = null;
  isOpen.value = false;
  emit("answerSubmitted");
});

// The unselected Respond button is aria-disabled but still tappable so the
// select-an-answer tooltip can open from the same tap.
function onRespondClick() {
  if (!selectedOptionId.value || voting.value) return;
  submitAnswer(selectedOptionId.value);
}

watch([isOpen, () => quizCardState.currentQuestion?.id], () => {
  selectedOptionId.value = null;
});
</script>
